import { ReactNode } from "react";

type Tone = "default" | "success" | "danger" | "warn" | "primary";

interface Props {
  label: string;
  /** The big number. Pass a string so the caller formats currencies,
   *  percentages, "—" placeholders, etc. */
  value: string;
  /** A small secondary line under the value (e.g. "vs 3% limit"). */
  hint?: string;
  /** Tone tints the value color. Card chrome stays neutral so the
   *  surface doesn't fight in either theme. */
  tone?: Tone;
  /** Inline glyph rendered top-right of the label. Pure decoration —
   *  keeps the card from being a wall of numbers. */
  glyph?: ReactNode;
}

const TONE_CLASSES: Record<Tone, string> = {
  default: "text-fg",
  success: "text-success",
  danger: "text-danger",
  warn: "text-warn",
  primary: "text-primary",
};

export function KpiCard({ label, value, hint, tone = "default", glyph }: Props) {
  return (
    <section className="flex h-full flex-col justify-between rounded-xl border border-border bg-surface p-5">
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-medium uppercase tracking-[0.15em] text-fg-muted">
          {label}
        </span>
        {glyph && <span className="text-fg-muted/70">{glyph}</span>}
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span
          className={`font-mono text-4xl font-bold leading-none tracking-tight tabular-nums xl:text-5xl ${TONE_CLASSES[tone]}`}
        >
          {value}
        </span>
      </div>
      {hint && (
        <p className="mt-2 text-xs text-fg-muted">{hint}</p>
      )}
    </section>
  );
}
